import React, { useState, useEffect } from 'react';

interface SCORMContentRendererProps {
  payload: {
    file_url: string;
    version?: string;
    reporting?: {
      commit_interval_sec?: number;
    };
    completion_mapping?: {
      on?: string;
      completed_values?: string[];
    };
    offline_access?: {
      enabled?: boolean;
      download_on_import?: boolean;
      local_file_required?: boolean;
    };
  };
  file: {
    file_id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_url: string;
    options: any;
  };
}

export const SCORMContentRenderer: React.FC<SCORMContentRendererProps> = ({ payload, file }) => {
  const [scormContentPath, setScormContentPath] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Function để parse imsmanifest.xml và lấy đường dẫn content
  const parseManifestAndGetContentPath = async (manifestUrl: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(manifestUrl);
      const xmlText = await response.text();
      
      // Parse XML để tìm resource có type="webcontent" và adlcp:scormType="sco"
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Tìm resource chính (SCO)
      const resources = xmlDoc.querySelectorAll('resource[adlcp\\:scormType="sco"]');
      if (resources.length > 0) {
        const mainResource = resources[0];
        const href = mainResource.getAttribute('href');
        if (href) {
          // Lấy base path từ manifest URL
          const basePath = manifestUrl.substring(0, manifestUrl.lastIndexOf('/'));
          setScormContentPath(`${basePath}/${href}`);
        }
      }
    } catch (error) {
      console.error('Error parsing SCORM manifest:', error);
      // Fallback về đường dẫn mặc định
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Sử dụng file_url từ payload hoặc fallback về đường dẫn test
    const manifestUrl =
      file.options?.s_base_imsmanifest ? (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") +
        file.options?.s_base_imsmanifest : payload.file_url;
    parseManifestAndGetContentPath(manifestUrl);
  }, [payload.file_url, file.options?.s_base_imsmanifest]);
  
  return (
    <div className="text-center">
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 mb-4">
        <div className="mt-4">
          <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Nội dung SCORM
          </h4>
          <div className="border rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-96 bg-gray-100 dark:bg-gray-800">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                  <p className="text-gray-600 dark:text-gray-400">Đang tải nội dung SCORM...</p>
                </div>
              </div>
            ) : scormContentPath ? (
              <iframe
                src={scormContentPath}
                className="w-full h-96"
                title="SCORM Content"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            ) : (
              <div className="flex items-center justify-center h-96 bg-gray-100 dark:bg-gray-800">
                <div className="text-center">
                  <p className="text-gray-600 dark:text-gray-400">Không thể tải nội dung SCORM</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
